import { useCallback } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import type { PlanLabelPositionDTO, PlanLabelPositionModifyDTO } from '@api/fastAPI.schemas'
import {
  getGetPlanLabelPositionsQueryKey,
  useDeletePlanLabelPosition,
  useSetPlanLabelPosition,
} from '@api/plan-label-positions/plan-label-positions'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { usePersistentParams } from 'src/hooks'

/**
 * Persist a moved / rotated plan label position, or reset it back to the
 * automatic placement and orientation. Both update the positions query cache optimistically in
 * `onMutate` (cancelling any in-flight fetch first, so it can't race the
 * optimistic write), so the label stays where it was dropped while the
 * request runs; on error, the cache is rolled back to the pre-mutation
 * snapshot instead of refetching.
 */
const usePlanLabelPositionMutations = () => {
  const { projectId }: { projectId?: string } = usePersistentParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const queryKey = getGetPlanLabelPositionsQueryKey(projectId)

  const setMutation = useSetPlanLabelPosition({
    mutation: {
      onMutate: async ({ elementGuid, data: position }) => {
        await queryClient.cancelQueries({ queryKey })
        const previous = queryClient.getQueryData<PlanLabelPositionDTO[]>(queryKey)
        queryClient.setQueryData<PlanLabelPositionDTO[]>(queryKey, current => [
          ...(current ?? []).filter(entry => entry.elementGuid !== elementGuid),
          { elementGuid, x: position.x, y: position.y, orientation: position.orientation },
        ])
        return { previous }
      },
      onError: (error: AxiosError, _variables, context) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Beschriftung'), {
          variant: 'error',
        })
        if (context) queryClient.setQueryData(queryKey, context.previous)
      },
    },
  })

  const deleteMutation = useDeletePlanLabelPosition({
    mutation: {
      onMutate: async ({ elementGuid }) => {
        await queryClient.cancelQueries({ queryKey })
        const previous = queryClient.getQueryData<PlanLabelPositionDTO[]>(queryKey)
        queryClient.setQueryData<PlanLabelPositionDTO[]>(queryKey, current =>
          (current ?? []).filter(entry => entry.elementGuid !== elementGuid),
        )
        return { previous }
      },
      onError: (error: AxiosError, _variables, context) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Zurücksetzen der Beschriftung'), {
          variant: 'error',
        })
        if (context) queryClient.setQueryData(queryKey, context.previous)
      },
    },
  })

  const { mutate: setMutate } = setMutation
  const setLabelPosition = useCallback(
    (elementGuid: string, position: PlanLabelPositionModifyDTO) => {
      setMutate({ projectGuid: projectId as string, elementGuid, data: position })
    },
    [setMutate, projectId],
  )

  const { mutate: deleteMutate } = deleteMutation
  const resetLabelPosition = useCallback(
    (elementGuid: string) => {
      deleteMutate({ projectGuid: projectId as string, elementGuid })
    },
    [deleteMutate, projectId],
  )

  return { setLabelPosition, resetLabelPosition }
}

export default usePlanLabelPositionMutations
